import * as React from 'react'

import Calendar from '@components/calendar/calendar'
import MultiSelect from '@components/select/multiSelect'

import useI18n from '@store/i18n/useI18n'
import styled from '@theme/styled-components'
import { Formik } from 'formik'
import { isAfter, isBefore, endOfDay, startOfDay } from 'date-fns'

interface PictureFormikValues {
  selectedSteps: Option[]
  selectedThemes: Option[]
  declarationStartDate?: Date
  declarationEndDate?: Date
  pictureStartDate?: Date
  pictureEndDate?: Date
}

interface Props {
  steps: BusinessStep[]
  allPictures: BusinessDetailsPicture[]
  setSectionPictures: (p: BusinessDetailsPicture[]) => void
}

const initialValues: PictureFormikValues = {
  selectedSteps: [],
  selectedThemes: [],
  declarationStartDate: undefined,
  declarationEndDate: undefined,
  pictureStartDate: undefined,
  pictureEndDate: undefined,
}

const PictureFilters = ({ steps, allPictures, setSectionPictures }: Props) => {
  const i18n = useI18n()

  const filterAndSetPictures = (v: PictureFormikValues) =>
    setSectionPictures(
      allPictures.filter(
        (p) =>
          (v.selectedSteps.length === 0 || v.selectedSteps.find((s) => s.value === p.idStep)) &&
          (v.selectedThemes.length === 0 || v.selectedThemes.find((s) => s.value === p.idTheme)) &&
          (!v.declarationStartDate ||
            isAfter(endOfDay(new Date(p.dateDeclaration)), new Date(v.declarationStartDate))) &&
          (!v.declarationEndDate ||
            isBefore(startOfDay(new Date(p.dateDeclaration)), new Date(v.declarationEndDate))) &&
          (!v.pictureStartDate || isAfter(endOfDay(new Date(p.date)), new Date(v.pictureStartDate))) &&
          (!v.pictureEndDate || isBefore(startOfDay(new Date(p.date)), new Date(v.pictureEndDate)))
      )
    )

  return (
    <Formik initialValues={initialValues} onSubmit={filterAndSetPictures}>
      {({ values, setFieldValue, submitForm }) => {
        const stepsOptions: Option[] = React.useMemo(
          () => steps.map((s) => ({ value: s.id, label: s.wording })),
          [steps]
        )
        const themesOptions = React.useMemo(
          () =>
            steps.reduce((acc, cur) => {
              if (values.selectedSteps.find((s) => cur.id === s.value)) {
                acc.push(...cur.themes.map((t) => ({ value: t.id, label: t.wording })))
              }
              return acc
            }, [] as Option[]),
          [steps, values.selectedSteps]
        )

        React.useEffect(() => {
          const sth = values.selectedThemes.filter((th) => themesOptions.find((t) => t.value === th.value))
          changeAndSubmit('selectedThemes', sth)
        }, [themesOptions])

        const changeAndSubmit = (fieldName: string, e: any) => {
          setFieldValue(fieldName, e)
          submitForm()
        }

        return (
          <>
            <ChoicesContainer>
              <Libelle>{i18n.t('components.filterLibelles.step')}</Libelle>
              <MultiSelect
                options={stepsOptions}
                selected={values.selectedSteps}
                setSelected={(e) => changeAndSubmit('selectedSteps', e)}
                placeholder={i18n.t('components.menu.placeholder.step')}
                isClearable
              />
              <Libelle>{i18n.t('components.filterLibelles.theme')}</Libelle>
              <MultiSelect
                options={themesOptions}
                selected={values.selectedThemes}
                setSelected={(e) => changeAndSubmit('selectedThemes', e)}
                placeholder={i18n.t('components.menu.placeholder.theme')}
                isDisabled={values.selectedSteps.length === 0}
                isClearable
              />
              <Libelle>{i18n.t('components.filterLibelles.photoDate')}</Libelle>
              <Calendar
                startDate={values.pictureStartDate}
                setStartDate={(e) => changeAndSubmit('pictureStartDate', e)}
                endDate={values.pictureEndDate}
                setEndDate={(e) => changeAndSubmit('pictureEndDate', e)}
                placeholder={i18n.t('components.menu.placeholder.date')}
              />
              <Libelle>{i18n.t('components.filterLibelles.declarationDate')}</Libelle>
              <Calendar
                startDate={values.declarationStartDate}
                setStartDate={(e) => changeAndSubmit('declarationStartDate', e)}
                endDate={values.declarationEndDate}
                setEndDate={(e) => changeAndSubmit('declarationEndDate', e)}
                placeholder={i18n.t('components.menu.placeholder.dateDeclaration')}
              />
            </ChoicesContainer>
          </>
        )
      }}
    </Formik>
  )
}

export default PictureFilters

const ChoicesContainer = styled('div')<{ fromdetailbusiness?: boolean }>`
  display: flex;
  padding: 20px 0;
  flex-direction: column;
  background: ${(props) => props.theme.colors.filterBackgroundColor};
  ${(props) => !!props.fromdetailbusiness && 'border: 0px solid;'}
  ${(props) => !!props.fromdetailbusiness && 'border-bottom-right-radius: 20px;'}
    ${(props) => !!props.fromdetailbusiness && 'border-bottom-left-radius: 20px;'}
`
const Libelle = styled('div')`
  margin: 5px 10px;
  ${(props) => props.theme.fonts.lonelyPictureLibelle}
`
